import React, { useState, useEffect } from 'react';
import { AppHeader } from '@/components/AppHeader';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

export default function Terms() {
  const [user, setUser] = useState<User | null>(null);
  useNavigationHistory(); // Track navigation

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader user={user} onSignOut={handleSignOut} />

      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <p className="text-sm text-muted-foreground mb-8">Last Updated: January 2025</p>

          <section className="mb-8 bg-primary/5 border-2 border-primary/20 rounded-lg p-6">
            <h2 className="text-2xl font-bold mb-4 text-primary">Disclaimer</h2>
            <p className="mb-4">
              Therappi<sup className="text-[0.6em]">®</sup> is a wellbeing and self-development platform designed to support personal growth, emotional balance, 
              and psychological insight. It offers self-guided tools, reflections, and educational content based on integrative 
              principles drawn from psychology, neuroscience, energy work, and consciousness studies.
            </p>
            <p className="mb-4">
              Therappi<sup className="text-[0.6em]">®</sup> does not provide medical or psychological diagnosis, treatment, or crisis intervention. It is not a 
              substitute for professional therapy, medical advice, or emergency care.
            </p>
            <p className="mb-4">
              All content is intended for informational and supportive purposes only. While created by a qualified psychologist, 
              use of the app does not constitute a therapeutic relationship.
            </p>
            <p className="mb-4 font-semibold">
              If you are experiencing significant distress, are in crisis, or are concerned about your mental health, please 
              seek immediate support from a licensed mental health professional or contact emergency services in your area.
            </p>
            <div className="bg-background/50 rounded p-4 mb-4">
              <p className="font-semibold mb-2">Important:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Always consult a qualified healthcare provider before making changes to your mental health treatment, 
                    medication, or physical wellbeing routines.</li>
                <li>Therappi<sup className="text-[0.6em]">®</sup> may include mood and mental health tracking features, but it does not monitor users or respond 
                    to inputs in real time.</li>
                <li>By using this app, you agree that you are responsible for your own wellbeing and that the creators of 
                    Therappi<sup className="text-[0.6em]">®</sup> are not liable for any decisions made based on app content.</li>
              </ul>
            </div>
            <p className="text-sm text-muted-foreground">
              For full terms and privacy policies, please refer to this document and our <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">1. Agreement to Terms</h2>
            <p className="mb-4">
              Welcome to Therappi. By accessing or using our mental health and wellness application ("Service"), 
              you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, 
              please do not use the Service.
            </p>
            <p className="mb-4">
              These Terms constitute a legally binding agreement between you and Therappi Ltd, a business registered at 
              Thames House, 77-77a High Street, Esher, Surrey, UK, KT10 9PA ("Therappi", "we", "us", or "our").
            </p>
            <p className="mb-4">
              <strong>International Users:</strong> This Service is available globally. By using the Service 
              from outside the United Kingdom, you acknowledge that you are responsible for compliance with 
              local laws and regulations in your jurisdiction. Where local consumer protection laws provide 
              greater protections, those provisions shall apply.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">2. Age Requirements and Parental Consent</h2>
            <p className="mb-4">
              <strong>Minimum Age:</strong> Users must be at least 13 years old to use Therappi. Users aged 
              13-17 ("Minors") must obtain verifiable parental or legal guardian consent before creating an 
              account or using any features of the Service.
            </p>
            <p className="mb-4">
              <strong>Parental Responsibilities:</strong> Parents or legal guardians who provide consent for 
              Minors to use the Service:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">Accept full responsibility for the Minor's use of the Service</li>
              <li className="mb-2">Agree to monitor the Minor's account activity and wellbeing</li>
              <li className="mb-2">Understand that AI-generated content may not always be appropriate for all ages</li>
              <li className="mb-2">Acknowledge that Therappi is a wellness tool, not a substitute for professional mental health care for minors</li>
              <li className="mb-2">May request account deletion or data access at any time by contacting support@therappi.app</li>
              <li className="mb-2">Are responsible for payment obligations associated with the Minor's account</li>
            </ul>
            <p className="mb-4">
              <strong>Age Verification:</strong> We reserve the right to verify ages and parental consent at any 
              time. Accounts found to be in violation of age requirements will be immediately suspended or terminated. 
              False age representation may result in permanent account ban.
            </p>
            <p className="mb-4">
              <strong>Special Protections for Minors:</strong> We implement enhanced safety measures for Minor 
              accounts including restricted community features, additional content moderation, limited data collection, 
              and no targeted advertising.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">3. Medical Disclaimer and Mental Health Services</h2>
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 mb-4">
              <h3 className="text-lg font-semibold mb-2 text-destructive">CRITICAL: THIS IS NOT MEDICAL ADVICE OR THERAPY</h3>
              <p className="mb-2">
                Therappi is a wellness, self-help, and personal development tool. It is <strong>NOT</strong>:
              </p>
              <ul className="list-disc pl-6 mb-2">
                <li>A substitute for professional medical advice, diagnosis, or treatment</li>
                <li>A replacement for therapy, counseling, or psychiatric care</li>
                <li>An emergency service or crisis intervention tool</li>
                <li>Suitable for treating serious mental health conditions without professional support</li>
                <li>A regulated healthcare service or medical device</li>
                <li>Monitored by licensed mental health professionals in real-time</li>
              </ul>
              <p className="font-semibold mt-4">
                If you are experiencing a mental health crisis, please contact emergency services (999 in the UK, 
                911 in the US, or your local emergency number) or visit findahelpline.com for international crisis support.
              </p>
            </div>

            <p className="mb-4">
              <strong>No Professional Healthcare Relationship:</strong> Use of this Service does not create a 
              therapist-patient, psychologist-patient, doctor-patient, counsellor-client, or any other professional 
              healthcare or therapeutic relationship. We are not healthcare providers and do not employ licensed 
              mental health professionals to deliver services through the platform.
            </p>

            <p className="mb-4">
              <strong>Not Regulated as Healthcare:</strong> Therappi is not regulated by healthcare authorities 
              (including but not limited to the UK's Care Quality Commission (CQC), US Food and Drug Administration (FDA), 
              European Medicines Agency (EMA), or equivalent international regulatory bodies) and does not provide 
              regulated health services or medical devices.
            </p>

            <p className="mb-4">
              <strong>Limitations of AI Technology:</strong> Our AI features have significant limitations and cannot:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">Diagnose mental health conditions, disorders, or illnesses</li>
              <li className="mb-2">Provide treatment for medical or psychological conditions</li>
              <li className="mb-2">Assess suicide risk or provide crisis intervention</li>
              <li className="mb-2">Replace professional clinical judgment</li>
              <li className="mb-2">Guarantee accuracy - AI may occasionally produce inaccurate, inappropriate, or harmful content</li>
              <li className="mb-2">Be used as the sole support for mental health concerns</li>
            </ul>

            <p className="mb-4">
              <strong>When to Seek Professional Help:</strong> You should immediately contact qualified mental 
              health professionals or emergency services if you experience:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">Suicidal thoughts, self-harm urges, or thoughts of harming others</li>
              <li className="mb-2">Severe depression, anxiety, panic attacks, or other acute mental health symptoms</li>
              <li className="mb-2">Psychotic symptoms or loss of touch with reality</li>
              <li className="mb-2">Trauma requiring specialised intervention</li>
              <li className="mb-2">Substance abuse or addiction issues</li>
              <li className="mb-2">Eating disorders requiring medical attention</li>
              <li className="mb-2">Any medical or psychiatric emergency</li>
            </ul>

            <p className="mb-4">
              <strong>Professional Indemnity and Insurance:</strong> We do not carry professional indemnity insurance 
              as mental health service providers because we do not provide professional mental health services, therapy, 
              or medical treatment. We maintain general business liability insurance appropriate for technology services. 
              Users assume all risks associated with using this wellness tool and should seek appropriate professional 
              care for medical and mental health needs.
            </p>

            <p className="mb-4">
              <strong>International Healthcare Note:</strong> Healthcare regulations vary by jurisdiction. This Service 
              is not intended to replace locally regulated healthcare services in your country.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">4. User Accounts</h2>
            <p className="mb-4">
              You may need to register for an account to access certain features of the Service. You agree to provide
              accurate, current, and complete information during the registration process and to update such information
              to keep it accurate, current, and complete.
            </p>
            <p className="mb-4">
              You are responsible for safeguarding your password and for all activities that occur under your account.
              You agree to notify us immediately of any unauthorised use of your account or any other breach of security.
            </p>
            <p className="mb-4">
              We reserve the right to suspend or terminate your account if any information provided during the
              registration process or thereafter proves to be inaccurate, not current, or incomplete.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">5. Acceptable Use</h2>
            <p className="mb-4">
              You agree to use the Service only for lawful purposes and in a way that does not infringe the rights of
              or restrict or inhibit anyone else's use and enjoyment of the Service. Prohibited behaviour includes
              harassing or causing distress or inconvenience to any other user, transmitting obscene or offensive
              content, or disrupting the normal flow of dialogue within the Service.
            </p>
            <p className="mb-4">
              You must not misuse our Service by knowingly introducing viruses, trojans, worms, logic bombs, or other
              material that is malicious or technologically harmful. You must not attempt to gain unauthorised access
              to our Service, the server on which our Service is stored, or any server, computer, or database connected
              to our Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">6. Intellectual Property</h2>
            <p className="mb-4">
              The Service and its original content, features, and functionality are owned by Therappi Ltd and are
              protected by international copyright, trademark, patent, trade secret, and other intellectual property
              or proprietary rights laws.
            </p>
            <p className="mb-4">
              You are granted a limited, non-exclusive, non-transferable, revocable license to access and use the
              Service for your personal, non-commercial use. You must not reproduce, distribute, modify, create
              derivative works of, publicly display, publicly perform, republish, download, store, or transmit any
              of the material on our Service, except as follows:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">Your computer may temporarily store copies of such materials in RAM incidental to your accessing and viewing those materials.</li>
              <li className="mb-2">You may store files that are automatically cached by your Web browser for display enhancement purposes.</li>
              <li className="mb-2">You may print or download one copy of a reasonable number of pages of the Service for your own personal, non-commercial use and not for further reproduction, publication, or distribution.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">7. Subscription, Payments and Billing</h2>
            <p className="mb-4">
              <strong>Payment Processing Compliance:</strong> All payments are processed securely through Stripe, 
              a PCI DSS Level 1 certified payment processor. We comply with Payment Card Industry Data Security 
              Standards (PCI DSS). We do not store your complete payment card details on our servers.
            </p>

            <h3 className="text-lg font-semibold mb-2">7.1 Subscription Terms</h3>
            <p className="mb-4">By purchasing a subscription, you agree to:</p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">Pay all applicable fees as displayed at the time of purchase</li>
              <li className="mb-2">Automatic recurring billing at the end of each billing period unless cancelled</li>
              <li className="mb-2">Cancel before the renewal date to avoid being charged for the next period</li>
              <li className="mb-2">Prices are subject to change with 30 days notice for existing subscribers</li>
            </ul>

            <h3 className="text-lg font-semibold mb-2">7.2 Subscription Cancellation and Refund Policy</h3>
            <div className="bg-muted/50 border border-border rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold">Important: Refund policies vary by jurisdiction</p>
              
              <p className="mb-2"><strong>UK & EU Users (Consumer Rights):</strong></p>
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">You have a 14-day cooling-off period from the date of purchase to cancel and receive a full refund, UNLESS you explicitly agree to begin using the Service immediately and waive your right to cancel during this period.</li>
                <li className="mb-2">If you begin using the Service within the 14-day period, you may still cancel but any refund will be reduced proportionally based on the service already provided.</li>
                <li className="mb-2">After 14 days, cancellations will stop future billing but no refund will be provided for the current billing period already paid.</li>
                <li className="mb-2">You will retain access to the Service until the end of your current paid period.</li>
              </ul>

              <p className="mb-2"><strong>US & Other International Users:</strong></p>
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">All subscription fees are non-refundable once charged.</li>
                <li className="mb-2">You may cancel your subscription at any time to prevent future charges.</li>
                <li className="mb-2">Cancellation will take effect at the end of your current billing period.</li>
                <li className="mb-2">You will retain access to the Service until the end of your current paid period.</li>
                <li className="mb-2">No refunds will be provided for partial subscription periods or unused time.</li>
              </ul>

              <p className="mb-2"><strong>How to Cancel:</strong></p>
              <p className="mb-4">
              To cancel your subscription, visit your account settings or contact us at support@therappi.app 
              at least 24 hours before your next billing date. Cancellations made after billing has occurred 
              will not receive a refund but will prevent future charges.
              </p>
            </div>

            <h3 className="text-lg font-semibold mb-2">7.3 Billing Authorization</h3>
            <p className="mb-4">
              By providing payment information, you authorise us and Stripe to charge the payment method for all 
              fees incurred. You are responsible for maintaining valid and current payment information. You must 
              update your payment details promptly if they change.
            </p>

            <h3 className="text-lg font-semibold mb-2">7.4 Currency, Taxes and International Payments</h3>
            <p className="mb-4">
              All prices are displayed in GBP (British Pounds). International users may be charged in their local 
              currency based on current exchange rates. You are responsible for:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">Any applicable taxes, VAT, GST, or duties in your jurisdiction</li>
              <li className="mb-2">Currency conversion fees imposed by your bank or payment provider</li>
              <li className="mb-2">International transaction fees</li>
            </ul>

            <h3 className="text-lg font-semibold mb-2">7.5 Payment Disputes and Chargebacks</h3>
            <p className="mb-4">
              If you believe you have been incorrectly billed, contact us at support@therappi.app within 60 days 
              of the charge. Filing a chargeback without contacting us first may result in immediate account 
              suspension. We reserve the right to suspend service during chargeback investigations.
            </p>

            <h3 className="text-lg font-semibold mb-2">7.6 Failed Payments and Account Suspension</h3>
            <p className="mb-4">
              If payment fails due to insufficient funds, expired cards, or other reasons:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">We will attempt to retry the payment</li>
              <li className="mb-2">Your subscription may be suspended until payment is successful</li>
              <li className="mb-2">You remain responsible for any unpaid fees</li>
              <li className="mb-2">Continued payment failure may result in account termination</li>
            </ul>

            <h3 className="text-lg font-semibold mb-2">7.7 Free Trials</h3>
            <p className="mb-4">
              New users may receive a free trial period. Trial periods automatically convert to paid subscriptions 
              unless cancelled before the trial ends. You will be notified before being charged. You may only use 
              one free trial per user.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">8. AI Credits and Usage</h2>
            
            <h3 className="text-lg font-semibold mb-2">8.1 What Are AI Credits?</h3>
            <p className="mb-4">
              AI Credits are virtual currency used exclusively within Therappi to access AI-powered features, 
              including but not limited to: AI therapy chat, dream analysis, guidance interpretation, and other 
              AI-enhanced tools. Credits are consumed each time you use an AI feature.
            </p>

            <h3 className="text-lg font-semibold mb-2">8.2 Types of AI Credits</h3>
            <p className="mb-4">There are two types of AI Credits:</p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2"><strong>Monthly Credits:</strong> Included with certain subscription tiers. These credits reset monthly and do not roll over to the next billing period. Unused monthly credits expire at the end of each billing cycle.</li>
              <li className="mb-2"><strong>Purchased Credits:</strong> Additional credits purchased separately through our credit packs. These credits do not expire but must be used before monthly credits when available.</li>
            </ul>

            <h3 className="text-lg font-semibold mb-2">8.3 Credit Purchase Terms</h3>
            <div className="bg-primary/5 border-2 border-primary/20 rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold text-primary">IMPORTANT: Please Read Carefully</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>ALL CREDIT PURCHASES ARE FINAL AND NON-REFUNDABLE</strong></li>
                <li><strong>AI Credits have NO CASH VALUE and CANNOT be redeemed, exchanged, or converted to money</strong></li>
                <li><strong>AI Credits CANNOT be transferred, sold, or gifted to other users</strong></li>
                <li><strong>Purchased credits do not expire but are forfeited if your account is terminated or suspended</strong></li>
                <li><strong>We reserve the right to modify credit costs for AI features with reasonable notice</strong></li>
                <li><strong>Credits are consumed immediately upon using an AI feature and cannot be recovered or refunded if you are dissatisfied with AI-generated content</strong></li>
              </ul>
            </div>

            <h3 className="text-lg font-semibold mb-2">8.4 Credit Usage Priority</h3>
            <p className="mb-4">
              When you use AI features, credits are deducted in the following order:
            </p>
            <ol className="list-decimal pl-6 mb-4">
              <li className="mb-2">Monthly credits (if available and not expired)</li>
              <li className="mb-2">Purchased credits</li>
            </ol>
            <p className="mb-4">
              You can view your current credit balance and transaction history in your account settings.
            </p>

            <h3 className="text-lg font-semibold mb-2">8.5 Free Trial Credits</h3>
            <p className="mb-4">
              New users receive 200 free trial credits valid for 14 days from account creation. Trial credits 
              expire after 14 days regardless of usage. Only one free trial is permitted per user. Attempting
              to create multiple accounts to receive additional trial credits is prohibited and may result in 
              account termination.
            </p>

            <h3 className="text-lg font-semibold mb-2">8.6 Credit Forfeiture</h3>
            <p className="mb-4">
              You will forfeit all remaining credits (both monthly and purchased) if:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">Your account is terminated or suspended for violating these Terms</li>
              <li className="mb-2">You voluntarily close your account (no refunds will be provided for unused purchased credits)</li>
              <li className="mb-2">You initiate a chargeback or payment dispute</li>
            </ul>

            <h3 className="text-lg font-semibold mb-2">8.7 Credit Refund Exceptions</h3>
            <p className="mb-4">
              <strong>UK/EU Users:</strong> Within the 14-day cooling-off period (as described in Section 7.2), 
              you may request a refund for purchased credit packs that have NOT been used. Once credits have been 
              consumed by using AI features, no refund is possible even within the cooling-off period.
            </p>
            <p className="mb-4">
              <strong>All Users:</strong> We reserve the right to issue refunds or credit compensation at our sole 
              discretion in cases of:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">Verified technical errors or system failures that prevented proper credit delivery</li>
              <li className="mb-2">Unauthorised charges resulting from fraud or security breaches (subject to investigation)</li>
              <li className="mb-2">Extraordinary circumstances as determined by Therappi management</li>
            </ul>

            <h3 className="text-lg font-semibold mb-2">8.8 AI Service Limitations</h3>
            <p className="mb-4">
              We do not guarantee:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">The accuracy, completeness, or quality of AI-generated responses</li>
              <li className="mb-2">Uninterrupted availability of AI features</li>
              <li className="mb-2">That AI features will meet your specific requirements or expectations</li>
            </ul>
            <p className="mb-4">
              Credits consumed for AI interactions that produce unsatisfactory results, errors, or inappropriate 
              content are not refundable. AI technology is inherently imperfect and may occasionally produce 
              unexpected or unhelpful responses.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">9. Crisis Mode and Safety Features</h2>
            
            <h3 className="text-lg font-semibold mb-2">9.1 Automatic Crisis Detection</h3>
            <div className="bg-primary/5 border-2 border-primary/20 rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold text-primary">IMPORTANT: Crisis Mode Limitations</p>
              <p className="mb-4">
                Therappi includes automatic crisis detection technology that monitors AI conversations for high-risk 
                language patterns (e.g., expressions of suicidal ideation, self-harm, or severe distress). When detected:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>AI responses are immediately restricted to pre-approved safety templates</strong> - No open-ended AI generation during active crisis</li>
                <li><strong>Emergency resources are provided</strong> - Crisis hotlines and safety information displayed prominently</li>
                <li><strong>Crisis mode persists until you confirm safety</strong> - Mode does not auto-exit while risk language continues</li>
                <li><strong>All crisis events are logged</strong> - Timestamp, trigger content, and resolution tracked for safety oversight</li>
              </ul>
            </div>

            <h3 className="text-lg font-semibold mb-2">9.2 Crisis Mode Is NOT Professional Crisis Intervention</h3>
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold text-destructive">CRITICAL LIMITATIONS:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>No Real-Time Monitoring:</strong> Crisis Mode is automated; no human professional reviews or responds to detected crises</li>
                <li><strong>Detection May Fail:</strong> AI detection is imperfect and may miss crisis language or trigger false positives</li>
                <li><strong>Delayed Response:</strong> There may be delays in detection due to technical issues or system load</li>
                <li><strong>Not a Crisis Service:</strong> This feature provides self-help resources only, not professional crisis intervention</li>
                <li><strong>You Are Responsible:</strong> You must seek immediate professional help during mental health emergencies - do not rely solely on this app</li>
              </ul>
            </div>

            <h3 className="text-lg font-semibold mb-2">9.3 Crisis Data Logging and Privacy</h3>
            <p className="mb-4">
              When Crisis Mode activates, we log:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">Timestamp of detection and resolution</li>
              <li className="mb-2">Trigger content (risk phrases detected)</li>
              <li className="mb-2">Risk level assessed by AI</li>
              <li className="mb-2">Which agent/feature triggered crisis mode (Therappi AI, Soul Guidance, Dream Analysis)</li>
              <li className="mb-2">Exit method (user confirmed safety, timeout, or manual override)</li>
            </ul>
            <p className="mb-4">
              These logs are stored securely, encrypted, and retained for 90 days for safety oversight and service improvement. 
              Crisis logs may be reviewed by authorised personnel for quality assurance but are never shared with third parties 
              unless required by law. For full details, see our <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>.
            </p>

            <h3 className="text-lg font-semibold mb-2">9.4 Crisis Suite Self-Help Tools</h3>
            <p className="mb-4">
              The Crisis & Safety Suite includes self-directed tools such as:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">Safety Plan Builder (personalised crisis response plan)</li>
              <li className="mb-2">Hope Kit (reasons for living, coping strategies)</li>
              <li className="mb-2">Grounding exercises and nervous system reset tools</li>
              <li className="mb-2">Emergency contact lists and crisis hotline directories</li>
            </ul>
            <p className="mb-4">
              <strong>These tools are for use between professional sessions or alongside professional care.</strong> They do not 
              replace professional crisis intervention, therapy, or emergency services. Always contact emergency services for 
              immediate danger.
            </p>

            <h3 className="text-lg font-semibold mb-2">9.5 User Responsibility and Liability</h3>
            <p className="mb-4">
              By using Crisis Mode and the Crisis Suite, you acknowledge and agree that:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">You understand these features are self-help tools, not professional services</li>
              <li className="mb-2">You will seek professional help for serious mental health concerns or emergencies</li>
              <li className="mb-2">You are responsible for your own safety and wellbeing</li>
              <li className="mb-2">Therappi Ltd and its operators are not liable for any outcomes from using Crisis Mode or related features</li>
              <li className="mb-2">You will not rely solely on the app during active mental health emergencies</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">9.6 Telempathy, Spiritual & Reproductive Health Content Disclaimer</h2>
            <div className="bg-primary/5 border-2 border-primary/20 rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold text-primary">IMPORTANT: Spiritual & Health Content Limitations</p>
              <p className="mb-4">
                Therappi includes telempathy tools, spiritual practices (tarot, oracle cards, psychic development), 
                lunar/astrological tracking, and reproductive health features (menstrual cycle tracking, fertility insights, 
                pregnancy/postpartum support, menopause tracking). These features are provided for:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Educational and Exploratory Purposes Only:</strong> Content is for personal growth, self-discovery, and spiritual exploration</li>
                <li><strong>NOT Medical, Psychological, or Professional Spiritual Guidance:</strong> These tools do not replace professional medical advice, licensed mental health care, fertility specialists, OB/GYNs, or qualified spiritual advisors/psychic mediums</li>
                <li><strong>NOT Contraceptive or Fertility Medical Devices:</strong> Menstrual and fertility tracking are NOT FDA-approved medical devices and must not be relied upon for contraception, family planning decisions, or medical diagnosis</li>
                <li><strong>NO Predictive Medical Value:</strong> Astrological, lunar, and spiritual insights have no scientifically validated predictive medical value</li>
                <li><strong>Personal Interpretation Required:</strong> All spiritual content (tarot readings, psychic exercises, dream analysis) requires personal interpretation and should not be taken as factual predictions or professional guidance</li>
              </ul>
            </div>

            <h3 className="text-lg font-semibold mb-2">Reproductive Health Specific Warnings</h3>
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold text-destructive">CRITICAL: Menstrual & Fertility Tracking Limitations</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>NOT Birth Control:</strong> Do NOT use menstrual cycle predictions or fertility windows as contraception. These predictions are estimates and may be inaccurate</li>
                <li><strong>NOT for Medical Diagnosis:</strong> Do NOT use cycle tracking to diagnose conditions like PCOS, endometriosis, or hormonal disorders - seek professional medical evaluation</li>
                <li><strong>Pregnancy & Postpartum:</strong> All pregnancy milestone tracking and postpartum content is educational only - always consult your healthcare provider for prenatal and postnatal care</li>
                <li><strong>IVF & Fertility Treatment:</strong> If undergoing IVF or fertility treatments, follow your specialist's guidance - app tracking is supplementary only</li>
                <li><strong>Menopause Tracking:</strong> Perimenopause and menopause symptom tracking does not replace hormone testing or medical consultation</li>
                <li><strong>Seek Professional Help For:</strong> Irregular cycles, severe pain, heavy bleeding, fertility concerns, pregnancy complications, postpartum depression, or any reproductive health concerns</li>
              </ul>
            </div>

            <h3 className="text-lg font-semibold mb-2">Telempathy & Spiritual Practice Warnings</h3>
            <div className="bg-primary/5 border border-primary/10 rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold">IMPORTANT: Spiritual Content Advisory</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Entertainment & Exploration:</strong> Tarot, oracle cards, psychic development tools, and lunar guidance are for entertainment, personal growth, and spiritual exploration</li>
                <li><strong>NOT Professional Psychic Readings:</strong> These tools do not replace professional psychic mediums, spiritual advisors, or trained tarot readers</li>
                <li><strong>Personal Responsibility:</strong> You are responsible for how you interpret and act upon spiritual insights - do not make major life decisions based solely on app-generated spiritual content</li>
                <li><strong>Mental Health Priority:</strong> If experiencing psychological distress, prioritise professional mental health care over spiritual practices</li>
                <li><strong>No Guarantees:</strong> We make no claims about the accuracy, effectiveness, or supernatural validity of spiritual content</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">9.7 AI Clinical Validation, Algorithm Transparency & Third-Party AI Providers</h2>
            
            <h3 className="text-lg font-semibold mb-2">AI Clinical Validation Disclosure</h3>
            <div className="bg-primary/5 border-2 border-primary/20 rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold text-primary">IMPORTANT: AI Is Not Clinically Validated</p>
              <p className="mb-4">
                <strong>No Clinical Trials or FDA Approval:</strong> Therappi's AI features (AI Therappi<sup className="text-[0.6em]">®</sup>, Dream Analysis, Soul Guidance) 
                have NOT undergone clinical trials, peer-reviewed validation studies, or regulatory approval by healthcare authorities 
                (FDA, EMA, MHRA, or equivalent). These AI tools are NOT medical devices and are NOT validated for therapeutic efficacy.
              </p>
              <p className="mb-4">
                <strong>Experimental Technology:</strong> AI technology is inherently experimental and may produce inaccurate, inappropriate, 
                or harmful responses. AI is not a substitute for evidence-based therapy, counseling, or medical treatment.
              </p>
              <p className="mb-4">
                <strong>No Therapeutic Claims:</strong> We make no claims that AI interactions will improve mental health outcomes, 
                reduce symptoms, or provide therapeutic benefit. Any perceived benefits are subjective and not medically validated.
              </p>
            </div>

            <h3 className="text-lg font-semibold mb-2">Crisis Detection Algorithm Transparency</h3>
            <div className="bg-muted/50 border border-border rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold">How Crisis Mode Detection Works:</p>
              <p className="mb-4">
                Our crisis detection algorithm uses natural language processing (NLP) to analyse conversation content for high-risk patterns:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Keyword Matching:</strong> Scans for critical phrases related to suicidal ideation, self-harm, severe distress (e.g., "want to die", "kill myself", "end it all")</li>
                <li><strong>Sentiment Analysis:</strong> Measures emotional tone on a scale from -1.0 (extremely negative) to +1.0 (extremely positive). Scores below -0.6 may trigger crisis mode</li>
                <li><strong>Contextual Analysis:</strong> Evaluates severity based on phrase combinations and context (e.g., "thinking about self-harm" vs. "worried I might hurt myself")</li>
                <li><strong>Risk Levels:</strong> Assigns risk levels (critical, high, medium, low) based on language severity and sentiment</li>
                <li><strong>Threshold Triggers:</strong> Crisis Mode activates automatically when critical or high-risk language is detected OR when sentiment score is extremely negative</li>
              </ul>
              <p className="mb-4 font-semibold text-destructive">
                <strong>CRITICAL LIMITATIONS:</strong> This algorithm is automated and imperfect. It may:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Miss Crisis Indicators</strong> (false negatives) - Fail to detect subtle or coded crisis language</li>
                <li><strong>Trigger Incorrectly</strong> (false positives) - Activate for non-crisis content that uses similar language</li>
                <li><strong>Delay Detection</strong> - May not activate immediately if crisis language is indirect or evolving</li>
                <li><strong>Cannot Replace Human Judgment</strong> - Automated systems cannot match trained crisis counsellors' assessment capabilities</li>
              </ul>
              <p className="mt-4 font-semibold">
                Do NOT rely on crisis detection as a safety net. If you are in crisis, contact emergency services immediately - 
                do not depend on the app to detect or respond to your needs.
              </p>
            </div>

            <h3 className="text-lg font-semibold mb-2">Third-Party AI Providers</h3>
            <div className="bg-muted/50 border border-border rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold">AI Technology Providers:</p>
              <p className="mb-4">
                Therappi uses third-party AI models to power conversational features. Your conversation data is processed by:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Anthropic Claude (Primary AI Provider):</strong> Powers AI Therappi<sup className="text-[0.6em]">®</sup>, Dream Analysis, and Soul Guidance through Anthropic's API. Claude models process your messages to generate responses. Data handling governed by Anthropic's Privacy Policy and Enterprise Data Protection Addendum.</li>
                <li><strong>Google Gemini (Secondary AI Provider):</strong> May be used for specific features, analytics, or as backup AI processing. Data handling governed by Google's Privacy Policy and Google Cloud Data Processing Terms.</li>
              </ul>
              <p className="mb-4 font-semibold">What This Means for Your Data:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Data Sharing:</strong> Your conversation content is transmitted to these third-party AI providers for processing</li>
                <li><strong>Encryption:</strong> All data is encrypted in transit (HTTPS/TLS) when sent to AI providers</li>
                <li><strong>No Training on Your Data (Anthropic):</strong> Anthropic does not train Claude models on customer data submitted via enterprise APIs</li>
                <li><strong>Retention:</strong> AI providers may temporarily cache conversation data for processing but do not permanently store individual conversations (as per their enterprise terms)</li>
                <li><strong>Your Control:</strong> You can delete your conversation history at any time via Settings → Privacy → Clear AI Memory</li>
              </ul>
              <p className="mb-4">
                For full details on how these providers handle data, review their privacy policies:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li><a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Anthropic Privacy Policy</a></li>
                <li><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Privacy Policy</a></li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">9.8 Biometric Data & State-Specific Laws (BIPA & State Biometric Laws)</h2>
            <div className="bg-primary/5 border-2 border-primary/20 rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold text-primary">WHOOP Biometric Data Collection & State Law Compliance</p>
              <p className="mb-4">
                When you connect your WHOOP account to Therappi, we collect sensitive biometric identifiers as defined by various 
                state biometric privacy laws, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Heart Rate Variability (HRV):</strong> Biometric identifier derived from cardiac rhythm patterns</li>
                <li><strong>Resting Heart Rate & Active Heart Rate:</strong> Physiological biometric measurements</li>
                <li><strong>Sleep Patterns:</strong> Sleep stage data (REM, deep sleep, light sleep) constitutes physiological biometric information</li>
                <li><strong>Strain & Recovery Metrics:</strong> Derived biometric indicators calculated from physiological data</li>
              </ul>
            </div>

            <h3 className="text-lg font-semibold mb-2">Illinois BIPA (Biometric Information Privacy Act) Compliance</h3>
            <div className="bg-muted/50 border border-border rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold">Illinois Residents - Additional Protections:</p>
              <p className="mb-4">
                If you are an Illinois resident, you have specific rights under 740 ILCS 14/ (Illinois Biometric Information Privacy Act):
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Informed Consent:</strong> Before collecting biometric data via WHOOP integration, we obtain your explicit written consent through our WHOOP authorization dialog</li>
                <li><strong>Purpose Disclosure:</strong> We disclose the specific purpose (wellness insights, mind-body correlation analysis) and duration (while WHOOP integration is active) for collecting, storing, and using your biometric data</li>
                <li><strong>No Sale or Profit:</strong> We will NEVER sell, lease, trade, or otherwise profit from your biometric identifiers or biometric information</li>
                <li><strong>Retention Schedule:</strong> Biometric data is retained only while your WHOOP integration is active. Upon disconnection, you can choose to delete all stored WHOOP data immediately</li>
                <li><strong>Security Standards:</strong> We use industry-standard encryption (AES-256 at rest, TLS 1.3 in transit) and access controls to protect biometric data</li>
                <li><strong>Private Right of Action:</strong> Illinois residents have the right to sue for violations of BIPA and may be entitled to statutory damages</li>
              </ul>
              <p className="mb-4 font-semibold">Your BIPA Rights:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access your biometric data via Settings → Integrations → WHOOP Data Rights → Download Data</li>
                <li>Request deletion via Settings → Integrations → WHOOP Data Rights → Delete WHOOP Data</li>
                <li>Withdraw consent by disconnecting WHOOP integration at any time</li>
                <li>File complaints with the Illinois Attorney General's Office: illinoisattorneygeneral.gov</li>
              </ul>
            </div>

            <h3 className="text-lg font-semibold mb-2">Other State Biometric Laws</h3>
            <div className="bg-muted/50 border border-border rounded-lg p-6 mb-4">
              <p className="mb-4">
                <strong>Texas (Business & Commerce Code § 503.001):</strong> Texas residents must provide consent for biometric data collection. 
                You consent through our WHOOP authorization dialog and can revoke consent by disconnecting WHOOP integration.
              </p>
              <p className="mb-4">
                <strong>Washington (RCW 19.375):</strong> Washington residents are notified of biometric data collection purposes and 
                have the right to opt-out (disconnect WHOOP integration) without penalty.
              </p>
              <p className="mb-4">
                <strong>Arkansas, California, New York, Maryland, Others:</strong> Where state laws provide additional biometric data 
                protections, those protections apply to residents of those states. We comply with all applicable state biometric privacy laws.
              </p>
              <p className="mb-4 font-semibold">
                To exercise your state-specific biometric privacy rights, contact us at support@therappi.app with your state of residence.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">9.9 Adverse Event Reporting & Duty to Warn</h2>
            
            <h3 className="text-lg font-semibold mb-2">Adverse Event Reporting</h3>
            <div className="bg-muted/50 border border-border rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold">Report Harmful Outcomes:</p>
              <p className="mb-4">
                If you experience any adverse outcomes related to using Therappi, including but not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Worsening mental health symptoms</li>
                <li>Harmful AI-generated advice or responses</li>
                <li>Crisis mode detection failures</li>
                <li>Inappropriate or triggering content</li>
                <li>Technical failures during crisis situations</li>
                <li>Any harm resulting from reliance on app content</li>
              </ul>
              <p className="mb-4">
                Please report immediately to: <strong>support@therappi.app</strong> with subject line "ADVERSE EVENT REPORT"
              </p>
              <p className="mb-4">
                We investigate all adverse event reports and take corrective action where necessary. Reporting helps us improve safety 
                for all users.
              </p>
            </div>

            <h3 className="text-lg font-semibold mb-2">Duty to Warn Policy (Tarasoff Doctrine)</h3>
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold text-destructive">CRITICAL: No Duty to Warn Capability</p>
              <p className="mb-4">
                <strong>We Are NOT Healthcare Providers:</strong> Therappi does not provide clinical mental health services, and therefore 
                has no legal "duty to warn" or "duty to protect" as established in Tarasoff v. Regents of the University of California (1976).
              </p>
              <p className="mb-4">
                <strong>No Real-Time Monitoring:</strong> While we have automated crisis detection, there is NO human monitoring of conversations. 
                We cannot and do not intervene in crisis situations. We cannot warn third parties or authorities of imminent danger.
              </p>
              <p className="mb-4 font-semibold">
                <strong>Your Responsibility:</strong> If you intend to harm yourself or others, YOU must immediately contact emergency services. 
                Do not rely on this app to protect you or others from harm. We cannot fulfil a duty to warn as we are not a healthcare provider 
                with a therapeutic relationship.
              </p>
              <p className="mb-4">
                <strong>Legal Disclosure Exception:</strong> We may disclose information to authorities ONLY if legally required by court order, 
                subpoena, or where disclosure is necessary to prevent imminent serious harm and we become aware of such harm through other means 
                (e.g., adverse event report). This is not a substitute for professional duty to warn obligations.
              </p>
            </div>

            <h3 className="text-lg font-semibold mb-2">Professional Boundaries</h3>
            <div className="bg-primary/5 border border-primary/10 rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold">No Therapeutic Relationship with Therappi:</p>
              <p className="mb-4">
                <strong>Technology Provider Only:</strong> Use of Therappi does not create a therapist-client, doctor-patient, counsellor-client, 
                or any professional healthcare relationship with Therappi Ltd or its representatives. We are a technology provider, not a healthcare provider.
              </p>
              <p className="mb-4">
                <strong>For Clients in Existing Therapy:</strong> If you are seeing a licensed mental health professional separately, using Therappi 
                does not create a dual relationship or conflict of interest with that professional. Therappi should be used as a supplementary self-help 
                tool between sessions, with your therapist's awareness and approval. Do not use Therappi to replace or avoid professional care.
              </p>
              <p className="mb-4">
                <strong>Professional Supervised Use:</strong> Licensed mental health professionals may use Therappi as a supplementary clinical tool with their own clients. 
                When used in a clinical context, the professional maintains full clinical responsibility. The therapeutic relationship exists between the client and their clinician, not with Therappi. 
                Clinicians must obtain appropriate informed consent and maintain professional liability insurance. Therappi serves as a technology tool only, not as the provider of therapy.
              </p>
              <p className="mb-4">
                <strong>No Professional Liability:</strong> We do not carry professional liability insurance for mental health services because 
                we do not provide professional mental health services. We maintain general business liability insurance appropriate for technology services.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">9.10 Insurance Coverage, Accessibility & Vulnerable Populations</h2>
            
            <h3 className="text-lg font-semibold mb-2">Insurance & Payment Coverage</h3>
            <div className="bg-muted/50 border border-border rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold">NOT Covered by Health Insurance:</p>
              <p className="mb-4">
                <strong>No Insurance Reimbursement:</strong> Therappi is NOT a healthcare service and is NOT eligible for reimbursement by 
                health insurance, Medicare, Medicaid, or health savings accounts (HSAs/FSAs) in most jurisdictions. Subscription fees are 
                personal expenses and not medical deductions.
              </p>
              <p className="mb-4">
                <strong>Self-Pay Only:</strong> All payments are out-of-pocket personal expenses. Do not submit claims to your insurance 
                provider as Therappi is not a covered healthcare service.
              </p>
              <p className="mb-4">
                <strong>Exception:</strong> In some jurisdictions, wellness apps may qualify for HSA/FSA reimbursement at the discretion 
                of your plan administrator. Contact your benefits provider for clarification - we cannot provide guidance on insurance coverage.
              </p>
            </div>

            <h3 className="text-lg font-semibold mb-2">Vulnerable Population Protections</h3>
            <div className="bg-primary/5 border-2 border-primary/20 rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold text-primary">Special Considerations for Vulnerable Users:</p>
              <p className="mb-4">
                The following populations should exercise extreme caution when using Therappi and must prioritise professional care:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Minors (Under 18):</strong> Must have parental supervision. Not suitable for crisis situations without adult involvement</li>
                <li><strong>Individuals with Serious Mental Illness:</strong> Those with schizophrenia, bipolar disorder, severe depression, or psychotic disorders should use Therappi ONLY as supplement to professional psychiatric care</li>
                <li><strong>Individuals with Active Suicidal Ideation:</strong> If you are actively suicidal, do NOT rely on Therappi - contact emergency services immediately</li>
                <li><strong>Trauma Survivors:</strong> Content may be triggering. Use trauma-specific tools only with professional guidance</li>
                <li><strong>Eating Disorder Patients:</strong> Tracking tools may trigger disordered eating behaviours - use with professional oversight only</li>
                <li><strong>Substance Abuse/Addiction:</strong> Therappi is not a substitute for addiction treatment programs or recovery support</li>
                <li><strong>Pregnancy/Postpartum:</strong> Reproductive health tracking is educational only - seek professional prenatal/postnatal care</li>
                <li><strong>Cognitive Impairments:</strong> May have difficulty interpreting AI responses - use with caregiver support</li>
              </ul>
              <p className="mb-4 font-semibold text-destructive">
                <strong>CRITICAL:</strong> Vulnerable populations are at higher risk of harm from AI limitations, crisis detection failures, 
                and lack of professional oversight. We strongly recommend professional mental health care for all vulnerable users.
              </p>
            </div>

            <h3 className="text-lg font-semibold mb-2">Accessibility Compliance (WCAG/ADA)</h3>
            <div className="bg-muted/50 border border-border rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold">Accessibility Commitment:</p>
              <p className="mb-4">
                Therappi strives to comply with Web Content Accessibility Guidelines (WCAG) 2.1 Level AA and aims to be accessible 
                to users with disabilities in accordance with the Americans with Disabilities Act (ADA) and UK Equality Act 2010.
              </p>
              <p className="mb-4">
                <strong>Current Accessibility Features:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Responsive design compatible with screen readers</li>
                <li>Keyboard navigation support</li>
                <li>Sufficient color contrast ratios for visual impairments</li>
                <li>Alternative text for images</li>
                <li>Text resizing support</li>
              </ul>
              <p className="mb-4">
                <strong>Limitations:</strong> Some features (tarot card visualisations, kaleidoscope animations, certain interactive elements) 
                may not be fully accessible to all users with disabilities. We are continuously working to improve accessibility.
              </p>
              <p className="mb-4">
                <strong>Request Accommodations:</strong> If you encounter accessibility barriers, please contact support@therappi.app and we 
                will work to provide reasonable accommodations.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">10. The Sanctuary: Advanced Subscription Tier</h2>
            
            <h3 className="text-lg font-semibold mb-2">10.1 What is The Sanctuary?</h3>
            <p className="mb-4">
              The Sanctuary is an advanced subscription tier that provides enhanced therapeutic features beyond 
              standard THERAPPI<sup className="text-[0.6em]">®</sup> AI. It includes access to timeline integration, condition-specific tools, 
              specialised therapeutic modes (CBT, Psychotherapy, Timeline Insights), and personalised insights 
              based on your self-reported past experiences and mental health conditions.
            </p>

            <h3 className="text-lg font-semibold mb-2">10.2 Shared Conversation Memory & AI Memory Bank</h3>
            <p className="mb-4">
              All THERAPPI<sup className="text-[0.6em]">®</sup> AI services (standard and The Sanctuary) share the same conversation memory and history 
              through our AI Memory Bank system:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2"><strong>Continuous Memory:</strong> AI remembers previous conversations, preferences, and context across sessions</li>
              <li className="mb-2"><strong>Cross-Feature Memory:</strong> Memory is shared across AI Therappi, Soul Guidance, and Dream Analysis</li>
              <li className="mb-2"><strong>Encrypted Storage:</strong> All conversation data is encrypted at rest and in transit</li>
              <li className="mb-2"><strong>User Control:</strong> You can clear your memory bank at any time via Settings → Privacy → Clear AI Memory</li>
            </ul>
            <p className="mb-4">
              <strong>What's Logged:</strong> The AI stores conversation summaries, key topics discussed, emotional patterns, 
              and personalised insights to provide continuity. Individual messages are logged for safety monitoring (e.g., crisis 
              detection) and quality assurance. Conversations are retained indefinitely unless you request deletion.
            </p>

            <h3 className="text-lg font-semibold mb-2">10.3 The Sanctuary Exclusive Data & Timeline Integration</h3>
            <p className="mb-4">
              The Sanctuary subscription includes access to specialised features that use additional data you provide:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2"><strong>Past Self Timeline:</strong> Significant life events you record in your Past Self Timeline (pivotal moments, trauma, achievements, relationships)</li>
              <li className="mb-2"><strong>Mental Health Conditions:</strong> Self-reported conditions, diagnoses, and symptoms you track over time</li>
              <li className="mb-2"><strong>Symptom Tracking:</strong> Patterns in mood, energy, sleep, and other mental health indicators</li>
              <li className="mb-2"><strong>Condition-Specific Tools:</strong> Evidence-based exercises for anxiety, depression, trauma, OCD, stress, and grief</li>
              <li className="mb-2"><strong>Advanced Therapeutic Modes:</strong> CBT mode, Psychotherapy mode, and Timeline Integration mode that leverage your timeline and condition data for deeper personalised support</li>
            </ul>
            <p className="mb-4">
              This specialised data is only accessible to THERAPPI<sup className="text-[0.6em]">®</sup> AI when you have an active Sanctuary subscription. 
              It allows the AI to provide deeper, trauma-informed therapeutic support by understanding your unique history 
              and life context.
            </p>

            <h3 className="text-lg font-semibold mb-2">10.4 WHOOP Integration & Biometric Data</h3>
            <p className="mb-4">
              If you connect your WHOOP account to Therappi, we access all 6 WHOOP API scopes to provide comprehensive 
              mind-body insights. We collect and use the following biometric data:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2"><strong>Recovery Metrics:</strong> HRV (Heart Rate Variability), recovery scores, resting heart rate, recovery trends</li>
              <li className="mb-2"><strong>Sleep Data:</strong> Sleep duration, efficiency, sleep stages (deep, REM, light), sleep disturbances, sleep performance scores</li>
              <li className="mb-2"><strong>Strain & Activity:</strong> Daily strain scores, workout data, calories burned, active heart rate, heart rate zones</li>
              <li className="mb-2"><strong>Physiological Cycles:</strong> Recovery cycles, strain cycles, performance cycle data</li>
              <li className="mb-2"><strong>Profile Information:</strong> User ID, WHOOP account details, timezone, measurement preferences</li>
              <li className="mb-2"><strong>Body Measurements:</strong> Height, weight, maximum heart rate, body metrics used for accurate calorie and strain calculations</li>
            </ul>
            <p className="mb-4">
              <strong>Purpose:</strong> WHOOP data is used exclusively to provide insights into how your physical recovery correlates 
              with mental wellness patterns. We auto-populate daily check-ins with WHOOP metrics and display correlations between 
              recovery scores and emotional states.
            </p>
            <p className="mb-4">
              <strong>Your Rights:</strong> You can disconnect WHOOP integration, download all stored WHOOP data, or delete WHOOP data 
              at any time via Settings → Integrations → WHOOP Data Rights. WHOOP data is never sold or shared with third parties.
            </p>

            <h3 className="text-lg font-semibold mb-2">10.5 Downgrading from The Sanctuary</h3>
            <div className="bg-primary/5 border-2 border-primary/20 rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold text-primary">IMPORTANT: Data Access After Downgrade</p>
              <p className="mb-4">
                If you downgrade from The Sanctuary to standard THERAPPI<sup className="text-[0.6em]">®</sup> AI:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Your conversation memory and history will be PRESERVED</strong> - You retain full access to all past conversations</li>
                <li><strong>Timeline events, condition data, and symptom tracking will remain SECURELY STORED</strong> - Your data is not deleted</li>
                <li><strong>THERAPPI<sup className="text-[0.6em]">®</sup> AI will NO LONGER have access to your Sanctuary-specific data</strong> - The AI will revert to general support mode without knowledge of your timeline or conditions</li>
                <li><strong>You will lose access to The Sanctuary tools</strong> - Condition-specific exercises, advanced modes, and specialised features become unavailable</li>
                <li><strong>Data is IMMEDIATELY restored upon resubscription</strong> - If you resubscribe to The Sanctuary, all your timeline and condition data becomes accessible again</li>
              </ul>
              <p className="mt-4">
                <strong>You will be warned before confirming any downgrade</strong> to ensure you understand the changes 
                to AI capabilities. Your data security and privacy remain our top priority regardless of subscription tier.
              </p>
            </div>

            <h3 className="text-lg font-semibold mb-2">10.6 Data Retention and Deletion</h3>
            <p className="mb-4">
              All data collected through The Sanctuary (timeline events, conditions, symptoms) is:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">Stored securely with encryption both in transit and at rest</li>
              <li className="mb-2">Retained indefinitely unless you request deletion</li>
              <li className="mb-2">Accessible to you for export at any time through your account settings</li>
              <li className="mb-2">Deleted upon account closure or upon your request via support@therappi.app</li>
            </ul>
            <p className="mb-4">
              For full details on data handling, please review our <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">11. User Content</h2>
            <p className="mb-4">
              Users may be able to submit, upload, publish, or display content, such as text, images, and other
              materials ("User Content"). You retain ownership of any intellectual property rights that you hold in
              your User Content.
            </p>
            <p className="mb-4">
              By providing User Content, you grant us a non-exclusive, worldwide, royalty-free, transferable,
              sub-licensable license to use, reproduce, distribute, prepare derivative works of, display, and perform
              your User Content in connection with the Service and our business, including without limitation for
              promoting and redistributing part or all of the Service (and derivative works thereof) in any media
              formats and through any media channels.
            </p>
            <p className="mb-4">
              You are solely responsible for your User Content and the consequences of posting or publishing it. You
              represent and warrant that you have all necessary rights to grant the licenses in this section, and that
              your User Content does not infringe, misappropriate, or violate any third party’s patent, copyright,
              trademark, trade secret, moral rights, or other proprietary or intellectual property rights, or rights
              of publicity or privacy, or result in the violation of any applicable law or regulation.
            </p>
            <p className="mb-4">
              We reserve the right to remove or disable access to any User Content for any reason, including User
              Content that we consider to be in violation of these Terms or otherwise harmful to the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">12. Third-Party Links</h2>
            <p className="mb-4">
              The Service may contain links to third-party websites or resources. We are not responsible or liable
              for the availability or accuracy of such websites or resources, or the content, products, or services
              available from such websites or resources. Links to such websites or resources do not imply any
              endorsement by us of such websites or resources or the content, products, or services available from
              such websites or resources.
            </p>
            <p className="mb-4">
              You acknowledge sole responsibility for and assume all risk arising from your use of any such websites
              or resources.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">13. Termination</h2>
            <p className="mb-4">
              We may terminate or suspend your account and bar access to the Service immediately, without prior
              notice or liability, under our sole discretion, for any reason whatsoever, including without limitation
              if you breach the Terms.
            </p>
            <p className="mb-4">
              If you wish to terminate your account, you may simply discontinue using the Service.
            </p>
            <p className="mb-4">
              All provisions of the Terms which by their nature should survive termination shall survive termination,
              including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations
              of liability.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">14. Limitation of Liability</h2>
            <div className="bg-muted/50 border border-border rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold">MAXIMUM LIABILITY - PLEASE READ CAREFULLY:</p>
              <p className="mb-2">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL THERAPPI, ITS OPERATORS, 
                DIRECTORS, EMPLOYEES, AFFILIATES, AGENTS, OR SERVICE PROVIDERS BE LIABLE FOR:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-1">ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
                <li className="mb-1">ANY LOSS OF PROFITS, REVENUES, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES</li>
                <li className="mb-1">ANY HARM RESULTING FROM RELIANCE ON AI-GENERATED CONTENT, CRISIS MODE DETECTION FAILURES, OR WELLNESS GUIDANCE</li>
                <li className="mb-1">ANY MEDICAL, PSYCHOLOGICAL, EMOTIONAL, OR PHYSICAL HARM ARISING FROM USE OF THE SERVICE OR FAILURE TO SEEK PROFESSIONAL HELP</li>
                <li className="mb-1">ANY DAMAGES ARISING FROM CRISIS MODE FALSE POSITIVES, MISSED DETECTIONS, OR DELAYED RESPONSES</li>
                <li className="mb-1">ANY DAMAGES ARISING FROM INABILITY TO ACCESS OR USE THE SERVICE</li>
                <li className="mb-1">ANY DAMAGES ARISING FROM UNAUTHORISED ACCESS TO YOUR ACCOUNT OR DATA</li>
                <li className="mb-1">ANY HARM RESULTING FROM USER CONTENT OR INTERACTIONS WITH OTHER USERS</li>
                <li className="mb-1">ANY FAILURE TO OBTAIN PROFESSIONAL CARE WHEN NEEDED</li>
                <li className="mb-1">ANY ACTIONS TAKEN OR NOT TAKEN BASED ON SERVICE CONTENT</li>
              </ul>
              <p className="mb-4 font-semibold">
                OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATED TO THE SERVICE SHALL NOT 
                EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO US IN THE 12 MONTHS PRECEDING THE CLAIM, OR 
                (B) £100 GBP.
              </p>
              <p className="mb-4">
                <strong>BASIS OF THE BARGAIN:</strong> You acknowledge that we have set our prices and entered 
                into these Terms in reliance upon these limitations of liability, and that the same form an 
                essential basis of the bargain between us. These limitations shall apply regardless of whether 
                we have been advised of the possibility of such damages.
              </p>
              <p className="mb-4">
                <strong>STATUTORY RIGHTS:</strong> Nothing in these Terms shall exclude or limit liability for:
              </p>
              <ul className="list-disc pl-6">
                <li className="mb-1">Death or personal injury caused by our negligence</li>
                <li className="mb-1">Fraud or fraudulent misrepresentation</li>
                <li className="mb-1">Any other liability that cannot be excluded or limited under applicable law</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">15. Disclaimer</h2>
            <p className="mb-4">
              The Service is provided on an "AS IS" and "AS AVAILABLE" basis. The Service is provided without
              warranties of any kind, whether express or implied, including, but not limited to, implied warranties
              of merchantability, fitness for a particular purpose, non-infringement, or course of performance.
            </p>
            <p className="mb-4">
              Therappi Ltd does not warrant that a) the Service will function uninterrupted, secure, or available at
              any particular time or location; b) any errors or defects will be corrected; c) the Service is free
              of viruses or other harmful components; or d) the results of using the Service will meet your
              requirements.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">16. Governing Law and Jurisdiction</h2>
            <p className="mb-4">
              <strong>UK Users:</strong> These Terms shall be governed by and construed in accordance with the 
              laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the 
              courts of England and Wales.
            </p>

            <p className="mb-4">
              <strong>EU Users:</strong> For users in the European Union, nothing in these Terms affects your 
              rights as a consumer under EU consumer protection laws (Directive 2011/83/EU). You may have the 
              right to bring disputes before the courts of your country of residence. EU users may also access 
              the European Commission's Online Dispute Resolution platform at ec.europa.eu/consumers/odr.
            </p>

            <p className="mb-4">
              <strong>US Users:</strong> For users in the United States, these Terms shall be governed by the 
              laws of the State of Delaware, excluding its conflicts of law rules. Any disputes shall be resolved 
              through binding arbitration in accordance with the Commercial Arbitration Rules of the American 
              Arbitration Association, except where prohibited by law. California residents and residents of 
              states that prohibit mandatory arbitration retain the right to bring disputes in small claims court 
              or state/federal court.
            </p>

            <p className="mb-4">
              <strong>Other International Users:</strong> For users in other jurisdictions, these Terms shall be 
              governed by the laws of England and Wales to the extent permitted by local law. Where local laws 
              provide greater consumer protections or mandate different dispute resolution procedures, those 
              protections and procedures shall apply.
            </p>

            <p className="mb-4">
              <strong>Dispute Resolution:</strong> Before initiating any formal legal proceedings, you agree to 
              first attempt to resolve disputes informally by contacting us at support@therappi.com. We commit 
              to working with you in good faith to resolve any concerns.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">17. Changes To Terms</h2>
            <p className="mb-4">
              We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a
              revision is material, we will provide at least 30 days' notice prior to any new terms taking effect.
              What constitutes a material change will be determined at our sole discretion.
            </p>
            <p className="mb-4">
              By continuing to access or use our Service after those revisions become effective, you agree to be
              bound by the revised terms. If you do not agree to the new terms, please stop using the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">18. Assignment</h2>
            <p className="mb-4">
              You may not assign or transfer these Terms, by operation of law or otherwise, without our prior
              written consent. Any attempt by you to assign or transfer these Terms without such consent will be
              void.
            </p>
            <p className="mb-4">
              We may assign or transfer these Terms, at our sole discretion, without restriction.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">19. Severability</h2>
            <p className="mb-4">
              If any provision of these Terms is held to be invalid or unenforceable, such provision shall be
              struck and the remaining provisions shall be enforced.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">20. Waiver</h2>
            <p className="mb-4">
              Our failure to enforce any right or provision of these Terms will not be considered a waiver of
              those rights.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">21. Entire Agreement</h2>
            <p className="mb-4">
              These Terms constitute the entire agreement between us regarding our Service, and supersede and
              replace any prior agreements we might have had between us regarding the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">22. Contact Us</h2>
            <p className="mb-4">
              If you have any questions about these Terms, please contact us at support@therappi.app.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">23. GDPR Compliance</h2>
            <p className="mb-4">
              We are committed to protecting your personal data and complying with the General Data Protection
              Regulation (GDPR). Please refer to our Privacy Policy for detailed information on how we collect,
              use, and protect your personal data.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
